import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Navigate, useLocation, useNavigate, useNavigationType, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {LayoutSidebar} from '../../Layout';
import {Loading} from '../Loading';
import {ClientTabs} from '../client/ClientTabs';
import {AccessService} from '../../utils/AccessService';
import {LocalStorage} from '../../utils/LocalStorage';
import {APIClient} from '../../utils/APIClient';
import {notifyOnboardingProgress} from '../../utils/onboardingProgress';
import {anchorAttr} from '../../guides/anchors';
import {useReportModality} from '../../hooks/useReportModality';
import {useFicha21} from '../../context/UserModulesContext';
import {AgentWebSocketsClient} from '../../websockets/WebSocketsClient';
import {readClientIdFromUrl} from '../../utils/DataExplorerUrlParams';
import {loadExplorerFormData} from '../../utils/explorerFormData';
import {InformesApi, errorOf} from '../../api/informesApi';
import {DataExplorerUpgradePage} from '../data_explorer/DataExplorerUpgradePage';
import {ConfigureEventsModal} from '../data_explorer/ConfigureEventsModal';
import {InformeProvider, useInforme} from './state/InformeContext';
import {isGa4SplitKept} from './state/informeReducer';
import {FIELD_KINDS, MODES, serializeLayout} from './state/informeModel';
import {useInformeRender} from './state/useInformeRender';
import {useEventsCheck} from './state/useEventsCheck';
import {useSeedNewInforme} from './state/useSeedNewInforme';
import {reportHasNothingToExport} from './state/renderWarnings';
import {SettingsBar} from './settings/SettingsBar';
import {PanelsGrid} from './panels/PanelsGrid';
import {RenderWarningsNotice} from './RenderWarningsNotice';
import {EventsNotice} from './EventsNotice';
import {InformeHeaderBar} from './header/InformeHeaderBar';
import {InformePicker} from './header/InformePicker';
import {TemplateChip} from './folio/TemplateChip';
import {SaveInformeModal, PORTAL_ACTION_COPY, TEMPLATE_ACTION_UPDATE} from './modals/SaveInformeModal';
import {SaveTemplateModal} from './modals/SaveTemplateModal';
import {UnsavedChangesModal} from './modals/UnsavedChangesModal';
import {FieldsModal} from './modals/FieldsModal';
import {DuplicateInformeModal} from './modals/DuplicateInformeModal';
import {periodLabel} from './modals/saveSummaries';
import {buildPdfTableData, exportReportToPDF} from '../../utils/pdfExport';
import {groupsByFirstColumn, hidesEmptyRows, panelTableResults} from './panels/panelData';
import {
    PANEL_TYPES, dimensionNames, isTextPanel, metricNames, tableColumnsOf, textHasContent, withoutTextContent,
} from './state/informeModel';
import {ApplyTemplateConfirmModal} from './modals/ApplyTemplateConfirmModal';
import {shortcutFor} from './shortcuts';
import {applyClientChange} from './state/clientSwitch';
import {clientNameOf, defaultClientId} from './state/clientList';
import {clientHasGa4Account} from '../elements/FieldSearchEmpty';
import {useGa4CrossLevels} from './state/useGa4CrossLevels';
import {availablePresets} from './state/presets';
import {errorText} from './state/renderError';
import {worstTableOverflow} from './state/fieldsLimits';
import {panelPatchAfterAdding} from './state/addFieldFromPanel';
import {defaultTitleKey} from './state/panelLayout';
import {
    INTERNAL_NAV_STATE,
    clientInformesPath,
    entrySettled,
    informesPathFor,
    informesRedirect,
    isInternalNav,
    nextEditorInstance,
} from './informesPaths';
import {useLeaveGuard} from './state/useLeaveGuard';

// Ctrl+S / Cmd+S y Cmd+K: la regla vive en `shortcuts.js` (con su test). Se reexporta por compatibilidad.
export {shortcutFor};

/**
 * Con `clientId` la pantalla es la solapa Informes de la ficha de ese cliente (2.1): las solapas
 * van como primer hijo de `.section-content`, la posición que el CSS de la 2.1 ya estiliza como
 * segunda fila del topbar. La miga «Clientes › cliente» la pone el topbar solo, por la URL.
 */
function Shell({clientId = null, children}) {
    return (
        <div className="app-content">
            <LayoutSidebar/>
            <section className="section-content">
                {clientId ? <ClientTabs clientId={clientId}/> : null}
                {children}
            </section>
        </div>
    );
}

/**
 * La pantalla de Informes (modelo de Pablo: Informe = Ajustes + Plantilla, sobre la app de
 * bloques del backend). Convive con el Explorador de Datos: la modalidad de la agencia decide
 * CUÁL de las dos se ofrece en el menú; el módulo `DATA_EXPLORER` decide SI hay pantalla — los
 * números de un informe los calcula el query view del Explorador, así que sin ese módulo el
 * upsell es el del Explorador, que es correcto.
 *
 * La misma pantalla sirve las cuatro rutas (`/informes`, `/informes/:informeId` y, en la 2.1,
 * `/client/:id/informes[/:informeId]`): que sea el MISMO componente es lo que deja pasar de una a
 * otra sin perder el informe en pantalla (ver `informesPaths.js`).
 */
export function InformesPage() {
    const {t} = useTranslation();
    const location = useLocation();
    const navigationType = useNavigationType();
    const [hasAccess, setHasAccess] = useState(null);
    const {isClientReports, known} = useReportModality();
    const {ficha21, loaded: frameKnown} = useFicha21();
    const {id: routeClientId, informeId} = useParams();
    // La navegación (`location.key`) con la que el editor ya quedó en pantalla: desde ahí, las
    // redirecciones de entrada no corren hasta que llegue otra navegación de afuera (ver
    // `entrySettled`). Es lo que deja cambiar de marco sin desmontar el editor (C-rec-fe-informes-02).
    const [shownAt, setShownAt] = useState(null);
    const settled = entrySettled({shownAt, location, historyAction: navigationType});
    const ready = hasAccess !== null && known && frameKnown;
    const legacyInformeId = !settled && ready && ficha21 && !routeClientId && informeId ? informeId : null;
    const legacyTarget = useClientOfInforme(legacyInformeId);

    useEffect(() => {
        // Directo al endpoint y con `status !== 'OK'`: `AccessService.GetUserModules` sólo
        // ramifica sobre 'ERROR'/'OK', y un NETWORK_ERROR o un código tipado lo dejaban
        // esperando para siempre (C-informes-06).
        new APIClient(AccessService.GetUserModulesUrl(LocalStorage.fetchUserId())).call('GET')
            .then((data) => {
                if (!data || data.status !== 'OK') { setHasAccess(false); return; }
                const modules = (data.data && data.data.modules) || [];
                setHasAccess(modules.includes(AccessService.MODULE_DATA_EXPLORER));
            })
            .catch(() => setHasAccess(false));
    }, []);

    // La modalidad decide si esta pantalla existe (C-informes-22): en `DATA_EXPLORER`, a mano por
    // URL, todo respondería 404 uniforme. Con la modalidad sin conocer, se espera.
    if (known && !isClientReports) return <Navigate to="/data-explorer" replace/>;

    // UN solo `Shell` para la carga y el editor: en dos lugares distintos del árbol, las solapas se
    // desmontaban al terminar de cargar, repetían sus pedidos y la fila saltaba (C-rec-fe-informes-06).
    // Mientras carga, las solapas van con el marco del caché, para que no aparezcan de golpe.
    const shellClientId = ficha21 ? routeClientId : null;
    // `guideLoading`: las guías de Informes esperan a que el editor esté (review C-apr-guias-03).
    const loading = <Loading message={t('informes.loading_screen', 'Abriendo Informes…')} guideLoading/>;
    if (!settled) {
        // Hasta saber el marco no se decide nada: con `ficha21` todavía en false, un usuario de la
        // 2.1 rebotaría de su ficha a la pantalla de agencia (mismo criterio que `ClientLines`).
        if (!ready) return <Shell clientId={shellClientId}>{loading}</Shell>;
        if (!hasAccess) return <DataExplorerUpgradePage/>;
        const redirect = informesRedirect({ficha21, routeClientId, informeId, queryClientId: readClientIdFromUrl()});
        if (redirect) return <Navigate to={redirect} replace/>;
        if (legacyInformeId) {
            if (legacyTarget === undefined) return <Shell clientId={shellClientId}>{loading}</Shell>;
            if (legacyTarget) return <Navigate to={legacyTarget} replace/>;
            // No se pudo: el editor de siempre, en el MISMO lugar del árbol, se encarga del error.
        }
        setShownAt(location.key);
    }
    return (
        <Shell clientId={shellClientId}>
            <InformesEditor clientScoped={ficha21} routeClientId={shellClientId}/>
        </Shell>
    );
}

/**
 * `/informes/:informeId` en la 2.1 (el «Abrir» viejo del portal, un marcador) lleva a la ficha del
 * cliente del informe, y para saber cuál hay que pedirlo. Devuelve `undefined` mientras busca, la
 * dirección de la ficha, o `null` si no aplica o no se pudo. La respuesta se ata al id que se pidió:
 * si el id cambia, vuelve a «buscando» en vez de mostrar el resultado del anterior (C-rec-fe-informes-07).
 */
function useClientOfInforme(informeId) {
    const [result, setResult] = useState({informeId: null, target: null});

    useEffect(() => {
        if (!informeId) return undefined;
        let alive = true;
        const settle = (target) => { if (alive) setResult({informeId, target}); };
        InformesApi.get(informeId)
            .then((r) => settle(r && r.status === 'OK' && r.data && r.data.agency_client_id
                ? clientInformesPath(r.data.agency_client_id, informeId) : null))
            .catch(() => settle(null));
        return () => { alive = false; };
    }, [informeId]);

    if (!informeId) return null;
    return result.informeId === informeId ? result.target : undefined;
}

/**
 * `clientScoped`: el marco 2.1 — las direcciones del editor son las de la ficha del cliente y la
 * salida con cambios se avisa. `routeClientId`: el cliente de la ruta, o sea que la pantalla es la
 * solapa de su ficha (en la 2.1 también existe `/informes` a secas, sin solapas, como hasta ahora).
 * El marco (`Shell`) lo pone `InformesPage`.
 */
function InformesEditor({clientScoped, routeClientId}) {
    const {t} = useTranslation();
    const location = useLocation();
    const navigationType = useNavigationType();
    const [formData, setFormData] = useState(null);
    const [failed, setFailed] = useState(false);
    // En la pantalla de agencia el cliente llega en la query (la pestaña y los links del clásico).
    const clientFromQuery = useMemo(() => readClientIdFromUrl(), []);

    // Qué instancia del editor corresponde a la URL (ver `nextEditorInstance`). En el clásico nada
    // remonta —la pantalla queda como estaba—, pero la dirección se sigue igual: si no, al pasar a
    // la 2.1 una dirección vieja se leería como una navegación nueva y remontaría el editor.
    const [instance, setInstance] = useState({pathname: location.pathname, instance: 0});
    const current = nextEditorInstance(instance, {
        pathname: location.pathname,
        internal: !clientScoped || isInternalNav(location, navigationType),
    });
    if (current !== instance) setInstance(current);

    useEffect(() => {
        loadExplorerFormData().then((data) => (data ? setFormData(data) : setFailed(true)));
    }, []);

    if (failed) {
        return <div className="inf-page"><div className="inf-panel-state -error">{t('informes.load_error', 'No se pudieron cargar los datos del informe.')}</div></div>;
    }
    // `Loading` sin `message` dibuja un <p> vacío: la pantalla quedaba en blanco los ~40 s que
    // tarda el primer render, sin spinner ni texto (QA-VIS-informes-01).
    if (!formData) return <Loading message={t('informes.loading_data', 'Cargando tus clientes y campos…')} guideLoading/>;

    // El default sale de los clientes SELECCIONABLES: `agencyClients[0]` podía ser uno inactivo
    // («Acciones», 4702, en la Demo) y la pantalla abría en «No se pudo calcular el informe».
    // En la ficha manda la ruta. Es el valor INICIAL del provider: después el cliente es el del
    // estado, que cambia sin remontar (abrir un informe de otro cliente).
    const agencyClientId = routeClientId ? String(routeClientId) : defaultClientId(formData.agencyClients, clientFromQuery);
    return (
        <InformeProvider key={current.instance} agencyClientId={agencyClientId}>
            <InformesBody formData={formData} cameFromClient={Boolean(clientFromQuery)}
                          clientScoped={clientScoped} inClientFicha={Boolean(routeClientId)}/>
        </InformeProvider>
    );
}

function InformesBody({formData, cameFromClient, clientScoped, inClientFicha}) {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const {informeId} = useParams();
    const {state, actions, informeDirty, templateModified, sheetModified, unsavedWork} = useInforme();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [saveOpen, setSaveOpen] = useState(false);
    const [saveAsNew, setSaveAsNew] = useState(false);
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    // `null`, o el scope del modal de campos: la PALETA de la hoja (`{scope: 'sheet'}`) o las
    // columnas de UNA tabla (`{scope: 'table', uid}`). Era un booleano cuando toda tabla mostraba
    // la paleta entera y había un solo modal posible (card hwuk9PW7).
    const [fieldsOpen, setFieldsOpen] = useState(null);
    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [pendingOpen, setPendingOpen] = useState(null);
    // La plantilla que espera confirmación porque la hoja tiene textos escritos (M7).
    const [templateToConfirm, setTemplateToConfirm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [snapshotAt, setSnapshotAt] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pulseKey, setPulseKey] = useState(0);
    const lastTemplateRef = useRef(state.template);
    const formDataRef = useRef(formData);
    formDataRef.current = formData;
    const panelRefsRef = useRef([]);
    const pendingTemplateLayoutRef = useRef(null);
    const pendingAfterTemplateRef = useRef(false);
    // Los guards miran `unsavedWork` —lo que se PERDERÍA—, no los dirties: un informe nuevo nunca
    // estuvo guardado, pero recién abierto no tiene nada que perder (card eo7J0qds).
    const dirtyRef = useRef(false);
    dirtyRef.current = unsavedWork;
    // La fuente VIVA para sembrar un informe nuevo con campos que esa fuente admita. Va por ref
    // porque `loadInforme` es un `useCallback` estable: leerlo de `state` ahí congelaría el valor
    // del primer render (y meterlo en las deps remontaría el callback en cada cambio de ajustes).
    const sourceTypeRef = useRef(null);
    sourceTypeRef.current = state.settings.source_type;
    const clientIdRef = useRef(state.agencyClientId);
    clientIdRef.current = state.agencyClientId;
    const templateRef = useRef(state.template);
    templateRef.current = state.template;
    const informeIdRef = useRef(null);
    informeIdRef.current = state.informe ? state.informe.id : null;
    // Una respuesta de red que llega cuando esta instancia del editor ya no está (el usuario cambió
    // de cliente o se fue) no puede tocar la URL de la que la reemplazó, y una apertura que otra más
    // nueva dejó atrás no puede pisarla (review C-rec-fe-informes-03). `aliveRef` es la instancia;
    // `openSeqRef`, cuál fue la última apertura (abrir, informe nuevo o cambiar de cliente).
    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => { aliveRef.current = false; };
    }, []);
    const openSeqRef = useRef(0);
    // Tras elegir eventos, el resultado cambia sin que cambien ajustes ni plantilla.
    const [refreshKey, setRefreshKey] = useState(0);
    // La FOTO de las cuentas que recorre el modal de eventos, o `null` cerrado: el modal avanza por
    // índice, y una lista que cambiara con el modal abierto le haría saltear o repetir cuentas.
    const [eventsModalAccounts, setEventsModalAccounts] = useState(null);

    const clientName = useMemo(
        () => clientNameOf(formData.agencyClients, state.agencyClientId),
        [formData.agencyClients, state.agencyClientId],
    );
    // Con GA4 en el cliente, la búsqueda sin resultados del modal de campos explica por qué un
    // parámetro de GA4 puede no estar (tiene que estar registrado en la propiedad).
    const clientHasGa4 = useMemo(
        () => clientHasGa4Account(formData.adAccounts, state.agencyClientId),
        [formData.adAccounts, state.agencyClientId],
    );
    // Con qué niveles cruza su GA4 por grupo y anuncio: decide si el aviso «GA4 va a aparecer en filas
    // aparte» del modal de columnas sale (`ga4Split.js`).
    const ga4CrossLevels = useGa4CrossLevels(formData.adAccounts, state.agencyClientId);
    // Los paneles sugeridos de «Agregar» («Plataforma vs. GA4 por campaña»): sólo con GA4.
    const presets = useMemo(
        () => availablePresets({clientHasGa4, fieldsData: formData.fieldsData, sourceType: state.settings.source_type}),
        [clientHasGa4, formData.fieldsData, state.settings.source_type],
    );

    // El folio ya no describe UNA tabla, así que avisa por la peor: alcanza con que una sola se
    // pase para que el render devuelva 422 y la hoja entera no se calcule.
    const fieldsLimits = useMemo(() => worstTableOverflow(state.template.panels), [state.template.panels]);

    // Lo que el modal edita según desde dónde se abrió: la paleta de la hoja, o las columnas de
    // la tabla apuntada — en SU orden, que es el de la tabla y no el de la paleta.
    const editableFields = useMemo(() => {
        if (!fieldsOpen || fieldsOpen.scope !== 'table') return state.template.fields;
        const panel = state.template.panels.find((p) => p.uid === fieldsOpen.uid);
        if (!panel) return state.template.fields;
        // Con el helper y no con `panel.dimensions` a secas: una tabla guardada sin columnas
        // propias abriría el modal VACÍO, y aplicar la dejaría sin nada que mostrar.
        const columns = tableColumnsOf(panel, state.template.fields);
        return [
            ...columns.dimensions.map((name) => ({name, kind: FIELD_KINDS.DIMENSION})),
            ...columns.metrics.map((name) => ({name, kind: FIELD_KINDS.METRIC})),
        ];
    }, [fieldsOpen, state.template.fields, state.template.panels]);

    // Informe nuevo sembrado según el tipo de negocio del cliente (ver `useSeedNewInforme`).
    const {seed: seedNewInforme, cancel: cancelSeed} = useSeedNewInforme({
        actions,
        getCatalog: () => formDataRef.current && formDataRef.current.fieldsData,
        getSourceType: () => sourceTypeRef.current,
        getClientId: () => clientIdRef.current,
        getTemplate: () => templateRef.current,
    });

    // Las navegaciones del propio editor cambian la dirección sin cambiar de pantalla: llevan la
    // marca de internas para no remontar el editor ni disparar el aviso de salida (ver
    // `INTERNAL_NAV_STATE`). En la 2.1 la dirección es la de la ficha del cliente. Una instancia que
    // ya no está no navega: el `navigate` del router sigue andando después de desmontar.
    const goTo = useCallback((path) => {
        if (aliveRef.current) navigate(path, {replace: true, state: INTERNAL_NAV_STATE});
    }, [navigate]);
    const pathFor = useCallback(
        (clientId, id = null) => informesPathFor({clientScoped, clientId, informeId: id}),
        [clientScoped],
    );

    const loadInforme = useCallback((id) => {
        cancelSeed();
        openSeqRef.current += 1;
        const seq = openSeqRef.current;
        InformesApi.get(id).then((r) => {
            if (!aliveRef.current || seq !== openSeqRef.current) return;
            if (!r || r.status !== 'OK') {
                toast.error(t('informes.open_error', 'No se pudo abrir el informe.'));
                // Sin esto el editor quedaba sin paneles, sin tarjeta Agregar y con la URL del
                // informe que no se pudo abrir: sin salida más que escribir otra URL (FE-11). En la
                // ficha, sin salir de la ficha.
                seedNewInforme();
                setSnapshotAt(null);
                goTo(inClientFicha && clientIdRef.current ? clientInformesPath(clientIdRef.current) : '/informes');
                return;
            }
            actions.loadInforme(r.data);
            setSnapshotAt(r.data.last_snapshot_at || null);
            // Con el cliente DEL INFORME: abierto desde «Todos los clientes» o por una URL con otro
            // cliente, en la 2.1 la dirección pasa a la ficha del suyo (contrato §1). Todo informe
            // tiene cliente (`ClientReport._agency_client` no admite nulos).
            goTo(pathFor(r.data.agency_client_id, r.data.id));
        });
    }, [actions, goTo, pathFor, inClientFicha, t, seedNewInforme, cancelSeed]);

    // Entrada: por URL (`/informes/:id`) o informe nuevo con los campos por defecto del catálogo.
    useEffect(() => {
        if (informeId) loadInforme(informeId);
        else if (!state.template.fields.length && !state.template.panels.length) seedNewInforme();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cambió el marco con el editor abierto («Volver al Dashbo clásico» o «Probar el Dashbo 2.1»):
    // la página no lo desmonta (ver `entrySettled`), y acá la dirección pasa a la del marco nuevo con
    // una navegación interna, sin perder lo que había en pantalla (review C-rec-fe-informes-02).
    const frameRef = useRef(clientScoped);
    useEffect(() => {
        if (frameRef.current === clientScoped) return;
        frameRef.current = clientScoped;
        goTo(pathFor(clientIdRef.current, informeIdRef.current));
    }, [clientScoped, goTo, pathFor]);

    // Pulso del chip: cada mutación de la hoja (campos o paneles) remonta la animación.
    useEffect(() => {
        if (lastTemplateRef.current !== state.template
            && (lastTemplateRef.current.panels !== state.template.panels || lastTemplateRef.current.fields !== state.template.fields)) {
            setPulseKey((k) => k + 1);
        }
        lastTemplateRef.current = state.template;
    }, [state.template]);

    // Guardia nativa de «cambios sin guardar» al cerrar o recargar la pestaña (el Explorador la
    // registra igual; sin ella un F5 tira todo — C-informes-16). Con el dirty a secas, un F5 en la
    // pantalla recién abierta también disparaba el «¿Salir del sitio?» del navegador.
    useEffect(() => {
        if (!unsavedWork) return undefined;
        const guard = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
        window.addEventListener('beforeunload', guard);
        return () => window.removeEventListener('beforeunload', guard);
    }, [unsavedWork]);

    // Y el aviso al salir DENTRO de la app (el menú lateral, las solapas, la miga), sólo en la 2.1.
    const leaveGuard = useLeaveGuard(clientScoped, dirtyRef);

    // Cuando la foto termina, el backend avisa por Pusher (`client-reports-<cliente>`): acá se
    // escucha y se refresca la fecha de la foto (C-informes-30).
    const informeIdForPusher = state.informe ? state.informe.id : null;
    useEffect(() => {
        if (!informeIdForPusher || !state.agencyClientId) return undefined;
        const channelName = `client-reports-${state.agencyClientId}`;
        const channel = AgentWebSocketsClient.subscribe(channelName);
        if (!channel) return undefined;
        const onChange = (data) => {
            if (!data || !data.status) return;
            if (data.status === 'COMPLETED') {
                InformesApi.get(informeIdForPusher).then((r) => {
                    if (!r || r.status !== 'OK') return;
                    setSnapshotAt(r.data.last_snapshot_at || null);
                    toast.success(t('informes.snapshot_ready', 'La foto está lista: el cliente ya la ve en su portal.'));
                });
            } else if (data.status === 'FAILED') {
                toast.error(t('informes.snapshot_failed', 'La foto no se pudo generar. Puedes pedirla de nuevo.'));
            }
        };
        channel.bind('client-report-snapshot-changed', onChange);
        return () => {
            if (channel.unbind) channel.unbind('client-report-snapshot-changed', onChange);
            AgentWebSocketsClient.unsubscribe(channelName);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [informeIdForPusher, state.agencyClientId]);

    // Atajos de teclado (mockup + la paleta de vistas de hoy).
    const modalOpen = pickerOpen || saveOpen || templateModalOpen || !!fieldsOpen || duplicateOpen || !!pendingOpen
        || !!eventsModalAccounts || leaveGuard.blocked || !!templateToConfirm;
    useEffect(() => {
        const onKeyDown = (e) => {
            const shortcut = shortcutFor(e, {modalOpen, activeElement: document.activeElement});
            if (!shortcut) return;
            e.preventDefault();
            // Desde un bloque de texto: primero sale el foco, así el `onBlur` del editor vuelca lo
            // último que se escribió y el guardado lleva el texto entero.
            if (shortcut === 'save-from-text' && document.activeElement) document.activeElement.blur();
            if (shortcut === 'save' || shortcut === 'save-from-text') { setSaveAsNew(false); setSaveOpen(true); }
            if (shortcut === 'picker') setPickerOpen(true);
            // 'swallow' no abre nada: sólo evita que el navegador se quede con el atajo.
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [modalOpen]);

    const {result, loading, error} = useInformeRender({
        agencyClientId: state.agencyClientId,
        template: state.template,
        settings: state.settings,
        enabled: state.template.panels.length > 0,
        refreshKey,
    });

    // Cuentas del informe sin eventos de conversión elegidos (card UvIR4eHe).
    const eventsCheck = useEventsCheck({
        agencyClientId: state.agencyClientId,
        settings: state.settings,
        template: state.template,
        fieldsData: formData.fieldsData,
        adAccounts: formData.adAccounts,
        businessUnits: formData.businessUnits,
    });
    const openEventsModal = () => setEventsModalAccounts(eventsCheck.unconfigured);
    // El modal dice cuántas cuentas guardó DE VERDAD. Sólo entonces se vuelve a preguntar y se
    // recalcula: «Omitir» o la ✕ sin guardar nada no cambian ningún número, y un render completo
    // le pega a la API de cada cuenta (review C-INFCONV-04).
    const closeEventsModal = (savedCount = 0) => {
        setEventsModalAccounts(null);
        if (savedCount > 0) {
            eventsCheck.recheck();
            setRefreshKey((k) => k + 1);
        }
    };

    // «Métricas y dimensiones» aplicado. Si se abrió desde el selector de un gráfico («+ Agregar otra
    // métrica», C-05), el gráfico vuelve con el campo nuevo elegido: después de la paleta, para que el
    // reducer ya haya podado lo que se quitó en el mismo modal.
    const applyFieldsFrom = (opened, fields) => {
        const addFor = opened && opened.addFor;
        const panel = addFor ? state.template.panels.find((p) => p.uid === addFor.uid) : null;
        const patch = panel ? panelPatchAfterAdding(panel, state.template.fields, fields, addFor.kind) : null;
        actions.applyFields(fields, formData.fieldsData);
        if (patch) actions.updatePanel(addFor.uid, patch);
    };

    // ¿La hoja tiene algún bloque de texto escrito? Un texto no pasa por el motor, así que no está
    // en `result`, pero sale en el PDF y se pierde al aplicar otra plantilla.
    const hasWrittenText = state.template.panels.some((p) => isTextPanel(p) && textHasContent(p.content));

    // ¿Quedó algún bloque con datos? Deriva del MISMO `result` que alimenta al aviso de arriba del
    // folio, así que los dos no pueden contradecirse.
    const nothingToExport = useMemo(() => reportHasNothingToExport(error, result, {hasText: hasWrittenText}),
        [error, result, hasWrittenText]);

    // --- cliente ---------------------------------------------------------------------------
    const changeClient = (clientId) => {
        if (String(clientId) === String(state.agencyClientId)) return;
        // `applyClientChange` ya sabe si había un informe con cambios (sin informe cargado no hay
        // nada que descartar): el dirty a secas daba falso positivo en la pantalla recién abierta.
        const discarded = applyClientChange(state, String(clientId)).discardedChanges;
        openSeqRef.current += 1;
        actions.setClient(String(clientId));
        setSnapshotAt(null);
        // En la 2.1 el chip sólo existe en `/informes` a secas: elegir un cliente lleva a su ficha,
        // sin remontar (la plantilla se conserva, como siempre).
        goTo(pathFor(clientId));
        // El informe se cierra, pero la PLANTILLA se conserva (regla de producto): decir «los
        // cambios sin guardar se descartaron» a secas era falso — la hoja sigue en pantalla, y
        // desde el arreglo de C-informes-02 sigue además protegida (review C-informes-03).
        toast.info(discarded
            ? t('informes.client_changed_discarded', 'Cambiaste de cliente: se cerró el informe con sus cambios sin guardar y las cuentas se restablecieron. La plantilla, el período y los filtros se mantienen.')
            : t('informes.client_changed', 'Cambiaste de cliente: las cuentas se restablecieron. La plantilla, el período y los filtros se mantienen.'));
    };

    // --- abrir -----------------------------------------------------------------------------
    const openInforme = (informe) => {
        setPickerOpen(false);
        if (state.informe && state.informe.id === informe.id) return;
        if (dirtyRef.current) { setPendingOpen(informe); return; }
        loadInforme(informe.id);
    };
    const newInforme = () => {
        setPickerOpen(false);
        if (dirtyRef.current) { setPendingOpen({id: null}); return; }
        openSeqRef.current += 1;
        seedNewInforme();
        setSnapshotAt(null);
        goTo(pathFor(state.agencyClientId));
    };
    const resolvePending = () => {
        const pending = pendingOpen;
        setPendingOpen(null);
        if (!pending) return;
        if (pending.id) loadInforme(pending.id);
        else {
            openSeqRef.current += 1;
            seedNewInforme();
            setSnapshotAt(null);
            // El MISMO `navigate` que hace `newInforme()`. Sin él, descartar para arrancar uno nuevo
            // dejaba la URL en `/informes/<id viejo>` con el editor en blanco: un F5 volvía a cargar
            // el informe descartado y se leía como pérdida de datos DESPUÉS de un descarte explícito
            // (review C-informes-01).
            goTo(pathFor(state.agencyClientId));
        }
    };

    // Toast de error por CÓDIGO (mapa cerrado en i18n) — salvo los del validador de layout, donde
    // el mensaje del server es el único que dice qué arreglar (ver `renderError.js`); si el error
    // trae `panel_id`, nombra el panel (C-informes-10 / C-informes-25).
    const errorToast = (response, fallbackKey, fallbackText) => {
        const err = errorOf(response);
        let message = errorText(err, (code) => t(`informes.errors.${code}`, t(fallbackKey, fallbackText)), i18n.language);
        if (err.panelId) {
            const panel = state.template.panels.find((p) => p.id === err.panelId || p.uid === err.panelId);
            if (panel) message += ` (${panel.title || t(defaultTitleKey(panel.type), panel.type)})`;
        }
        toast.error(message);
    };

    // --- guardar informe ---------------------------------------------------------------------
    const layoutPayload = () => serializeLayout(state.template);
    const saveInforme = ({name, mode, alsoTemplate, templateAction, portalAction}) => {
        setSaving(true);
        const asNew = saveAsNew || !(state.informe && state.informe.id) || portalAction === PORTAL_ACTION_COPY;
        // Lo que VIAJA se captura acá: el baseline se calcula sobre esto, y la plantilla «también»
        // se guarda con esta hoja aunque el usuario ya haya abierto otro informe (C-informes-16).
        const sent = {settings: state.settings, template: state.template};
        const payload = {name, mode, settings: sent.settings, layout: layoutPayload(), template_id: state.template.id || null};
        const request = asNew
            ? InformesApi.create({...payload, client_id: state.agencyClientId})
            : InformesApi.update(state.informe.id, payload);
        request.then((r) => {
            // El usuario se fue con el guardado en vuelo: el informe SÍ se guardó y se dice, pero
            // esta instancia ya no toca ni la URL ni el estado (review C-rec-fe-informes-03).
            if (!aliveRef.current) {
                if (r && r.status === 'OK') toast.success(t('informes.saved', 'Informe guardado.'));
                return;
            }
            setSaving(false);
            if (!r || r.status !== 'OK') { errorToast(r, 'informes.save_error', 'No se pudo guardar el informe.'); return; }
            const saved = r.data;
            actions.savedInforme({id: saved.id, name: saved.name, mode: saved.mode, is_published: !!saved.is_published}, sent);
            setSnapshotAt(saved.last_snapshot_at || null);
            if (asNew) goTo(pathFor(saved.agency_client_id || state.agencyClientId, saved.id));
            setSaveOpen(false);
            toast.success(t('informes.saved', 'Informe guardado.'));
            // Un informe nuevo cumple «Arma tu primer informe»: el drawer se tilda sin recargar.
            if (asNew) notifyOnboardingProgress(r);
            if (alsoTemplate && templateAction === TEMPLATE_ACTION_UPDATE && state.template.id) {
                // Primero la plantilla, DESPUÉS el informe pendiente: si no, el PATCH estampaba la
                // procedencia sobre el informe recién abierto.
                updateTemplate(payload.layout).then(() => { if (pendingOpen) resolvePending(); });
                return;
            }
            if (alsoTemplate) {
                pendingTemplateLayoutRef.current = payload.layout;
                pendingAfterTemplateRef.current = !!pendingOpen;
                setTemplateModalOpen(true);
                return;
            }
            if (pendingOpen) resolvePending();
        });
    };

    // --- plantilla ---------------------------------------------------------------------------
    /**
     * Anota en el informe YA GUARDADO de qué plantilla viene. Guardar una plantilla desde el
     * informe le cambiaba la procedencia sólo en pantalla: al recargar, el chip volvía a «Sin
     * plantilla» porque el `source_template_id` nunca se había persistido (QA UI-12 / FE-19).
     * Es un PATCH de procedencia: lleva el layout para que el backend no re-copie la plantilla.
     */
    const linkTemplateToInforme = (templateId) => {
        const informe = state.informe;
        if (!templateId || !informe || !informe.id) return;
        InformesApi.update(informe.id, {template_id: templateId, layout: layoutPayload()});
    };

    // Una plantilla guarda los bloques de texto vacíos: lo escrito es de este cliente (C-04).
    const updateTemplate = (layout = null) => {
        if (!state.template.id) { setTemplateModalOpen(true); return Promise.resolve(); }
        return InformesApi.updateTemplate(state.template.id, {layout: withoutTextContent(layout || layoutPayload())}).then((r) => {
            if (!r || r.status !== 'OK') { errorToast(r, 'informes.template.save_error', 'No se pudo guardar la plantilla.'); return; }
            actions.savedTemplate({id: r.data.id, name: r.data.name, is_shared: r.data.is_shared});
            linkTemplateToInforme(r.data.id);
            toast.success(t('informes.template.updated', 'Plantilla actualizada.'));
        });
    };
    const createTemplate = ({name, is_shared}) => {
        setSaving(true);
        // La hoja capturada al guardar el informe, si venimos de «guardar también como plantilla».
        const layout = withoutTextContent(pendingTemplateLayoutRef.current || layoutPayload());
        pendingTemplateLayoutRef.current = null;
        InformesApi.createTemplate({name, is_shared, layout}).then((r) => {
            setSaving(false);
            if (!r || r.status !== 'OK') { errorToast(r, 'informes.template.save_error', 'No se pudo guardar la plantilla.'); return; }
            actions.savedTemplate({id: r.data.id, name: r.data.name, is_shared: r.data.is_shared});
            linkTemplateToInforme(r.data.id);
            setTemplateModalOpen(false);
            toast.success(t('informes.template.saved', 'Plantilla guardada.'));
            if (pendingAfterTemplateRef.current) { pendingAfterTemplateRef.current = false; resolvePending(); }
        });
    };
    const applyTemplateNow = (tpl) => {
        const apply = (data) => actions.applyTemplate({id: data.id, name: data.name, is_shared: data.is_shared, layout: data.layout});
        if (tpl.layout) { apply(tpl); return; }
        InformesApi.getTemplate(tpl.id).then((r) => {
            if (!r || r.status !== 'OK') { toast.error(t('informes.template.open_error', 'No se pudo aplicar la plantilla.')); return; }
            apply(r.data);
        });
    };
    // Con un texto escrito en la hoja, primero se pregunta: la plantilla lo reemplaza (M7).
    const applyTemplate = (tpl) => {
        if (hasWrittenText) { setTemplateToConfirm(tpl); return; }
        applyTemplateNow(tpl);
    };

    // --- portal ------------------------------------------------------------------------------
    const togglePublish = () => {
        const informe = state.informe;
        if (!informe || !informe.id) return;
        const request = informe.is_published ? InformesApi.unpublish(informe.id) : InformesApi.publish(informe.id);
        request.then((r) => {
            if (!r || r.status !== 'OK') { errorToast(r, 'informes.publish_error', 'No se pudo cambiar la publicación.'); return; }
            // Publicar no es guardar: sólo `is_published`, el dirty se conserva (C-informes-16).
            actions.publishOk(!informe.is_published);
            if (informe.is_published) {
                toast.success(t('informes.unpublished', 'El informe ya no está en el portal.'));
                return;
            }
            toast.success(r.data && r.data.snapshot_requested
                ? t('informes.published_snapshot', 'Informe asociado al portal. La foto se está generando: el cliente la verá en unos minutos.')
                : t('informes.published', 'Informe asociado al portal.'));
            // Asociarlo cumple «Comparte un informe en el portal» de Aprende Dashbo: se tilda sin volver
            // a la página (review C-apr-guias-09). Quitarlo no completa nada.
            notifyOnboardingProgress(r);
        });
    };
    const refreshSnapshot = () => {
        if (!state.informe || !state.informe.id) return;
        InformesApi.requestSnapshot(state.informe.id).then((r) => {
            if (!r || r.status !== 'OK') { toast.error(t('informes.snapshot_error', 'No se pudo pedir la foto.')); return; }
            toast.info(t('informes.snapshot_requested', 'La foto se está generando. Te avisamos cuando esté lista.'));
        });
    };
    // PDF: el mismo exportador del Explorador, con un ref por panel (edición aditiva `panelRefs`)
    // y la tabla del primer panel de tabla que haya rendido.
    const downloadPdf = async () => {
        const tablePanel = ((result && result.panels) || []).find((p) => p.type === PANEL_TYPES.TABLE && p.state === 'OK');
        // «Ocultar filas sin datos» es del panel de la PLANTILLA (el renderizado no trae `options`):
        // el PDF muestra las mismas filas que la pantalla.
        const tableTemplatePanel = tablePanel ? state.template.panels.find((p) => (p.id || p.uid) === tablePanel.id) : null;
        // El export toma el hilo principal a ratos y tarda ~50 s: sin la bandera, el usuario
        // apretaba de nuevo y encolaba un segundo export sobre el primero.
        setPdfLoading(true);
        try {
            const outcome = await exportReportToPDF(
                {panelRefs: panelRefsRef.current},
                {clientName, dateRange: periodLabel(state.settings.period, t), dimensions: dimensionNames(state.template.fields),
                 metrics: metricNames(state.template.fields), fieldsData: formData.fieldsData, t, locale: i18n && i18n.language,
                 // El exportador es el mismo del Explorador y sus textos son los de ESA pantalla: sin
                 // esto, un PDF bajado desde acá se titulaba «Informe — Explorador de Datos» y se
                 // guardaba como `reporte-explorador-…`. El encabezado lleva el nombre del informe
                 // («Informe nuevo» mientras no se guardó). La condición es la MISMA que la de la
                 // barra (`saved = !!(informe && informe.id)`, en `InformeHeaderBar`), no una
                 // equivalente: el PDF y el encabezado de pantalla no pueden nombrarlo distinto.
                 pdfTitle: (state.informe && state.informe.id && state.informe.name)
                     || t('informes.header.new_informe', 'Informe nuevo'),
                 fileBaseName: 'informe'},
                // Las notas de filtro van en el panel, al lado de `data` (card x4TLNeOw).
                // Y la tabla sale en el orden con que arranca en pantalla, agrupada si el panel lo pide.
                tablePanel ? buildPdfTableData(panelTableResults(tablePanel), {hideEmptyRows: hidesEmptyRows(tableTemplatePanel),
                    stableOrder: true, groupByFirstColumn: groupsByFirstColumn(tableTemplatePanel)}) : null,
            );
            // El export tarda ~50 s y no había forma de saber en qué bloque se van: el desglose
            // por panel queda en la consola para poder medirlo sin instrumentar nada.
            if (outcome && outcome.panelTimings) {
                console.debug('[pdfExport] ms por panel', outcome.panelTimings);
            }
            // Un panel que no se pudo rasterizar sale del PDF en silencio: sin esto el usuario
            // recibe un archivo con un panel menos y no tiene cómo saberlo.
            const omitidos = (outcome && outcome.skippedPanels) || [];
            if (omitidos.length) {
                toast.warning(t('informes.pdf_partial', {count: omitidos.length,
                    defaultValue: 'El PDF se descargó, pero {{count}} paneles no se pudieron incluir.'}));
            }
        } catch (e) {
            // `exportReportToPDF` puede rechazar entero (jsPDF, la precarga de imágenes): sin este
            // catch el botón volvía a su lugar, no bajaba nada y la pantalla no decía por qué.
            toast.error(t('informes.pdf_error', 'No se pudo armar el PDF.'));
        } finally {
            setPdfLoading(false);
        }
    };

    const previewAsClient = () => {
        if (!state.informe || !state.informe.id) return;
        window.open(`/client/${state.agencyClientId}/preview/reports?client_report=${state.informe.id}`, '_blank', 'noopener');
    };

    return (
        <div className="inf-page" data-testid="informes-page">
            <InformeHeaderBar
                agencyClients={formData.agencyClients}
                agencyClientId={state.agencyClientId}
                onClientChange={changeClient}
                cameFromClient={cameFromClient}
                inClientFicha={inClientFicha}
                informe={state.informe}
                informeDirty={informeDirty}
                snapshotAt={snapshotAt}
                onOpenPicker={() => setPickerOpen(true)}
                onSave={() => { setSaveAsNew(false); setSaveOpen(true); }}
                onSaveAsNew={() => { setSaveAsNew(true); setSaveOpen(true); }}
                onDuplicate={() => setDuplicateOpen(true)}
                onDownloadPdf={downloadPdf} pdfLoading={pdfLoading}
                /* El informe no se pudo calcular: el PDF saldría con todos los bloques en «No
                   disponible» y sin el motivo, que no viaja al archivo (review R-reports-fe-08).
                   ⚠️ `!!error || !result` NO alcanza: eso es «la llamada falló», y el caso central
                   de la card devuelve **200** con todos los paneles en ERROR — `error` es `null`,
                   `result` existe y el botón quedaba habilitado justo en el escenario que esto
                   venía a cerrar (QA `QA-VIS-err-04`). La pregunta es «¿quedó algún bloque con
                   datos?», y ésa la contesta `everyPanelFailed`. */
                pdfUnavailable={nothingToExport}
                onPublishToggle={togglePublish}
                onRefreshSnapshot={refreshSnapshot}
                onPreviewAsClient={previewAsClient}
            />
            <SettingsBar
                settings={state.settings}
                formData={formData}
                agencyClientId={state.agencyClientId}
                accountsResetAt={state.accountsResetAt}
                onChange={actions.setSettings}
                onClearAccountsReset={actions.clearAccountsReset}
                selectorKey={state.informe && state.informe.id ? state.informe.id : 'new'}
            />
            <div className={`inf-folio ${loading ? '-calculando' : ''}`} data-testid="informes-folio">
                {/* El ancla va en la FILA y no en el folio entero: la guía «Arma y guarda un informe»
                    explica qué es la plantilla, y con el folio el foco abarcaría toda la hoja. */}
                <div className="inf-folio-header" {...anchorAttr('informes_template')}>
                    <span className="inf-folio-eyebrow">{t('informes.template.eyebrow', 'PLANTILLA')}</span>
                    <TemplateChip
                        template={state.template}
                        templateModified={templateModified}
                        pulseKey={pulseKey}
                        onApply={applyTemplate}
                        onUpdate={updateTemplate}
                        onSaveAsNew={() => setTemplateModalOpen(true)}
                    />
                    <span className="inf-folio-hint">{t('informes.template.hint', 'Todo lo que está en esta hoja se guarda en la plantilla')}</span>
                    {/* Con datos ya en pantalla, un render nuevo sólo atenuaba el folio y se leía
                        como que la pantalla no había hecho nada. El aviso lo dice con palabras y
                        la barra del borde superior lo muestra mientras dura. */}
                    {loading && (
                        <span className="inf-calc" role="status" aria-live="polite" data-testid="informes-calculando">
                            <span className="inf-calc-spinner" aria-hidden="true"/>
                            {t('informes.calculating', 'Calculando…')}
                        </span>
                    )}
                    <span className="inf-spacer"/>
                    {/* El contador se marca cuando la selección pasa un tope del backend: es la
                        única señal del folio de que el informe no va a poder calcularse. */}
                    <button type="button" className={`inf-fields-button ${fieldsLimits.hasOverflow ? '-over' : ''}`}
                            title={fieldsLimits.hasOverflow ? t('informes.fields.over_hint', 'Alguna tabla del informe tiene campos de más. Ábrela con su «Editar» para quitar los que sobren.') : undefined}
                            onClick={() => setFieldsOpen({scope: 'sheet'})} data-testid="open-fields">
                        {t('informes.fields.button', {count: state.template.fields.length, defaultValue: 'MÉTRICAS Y DIMENSIONES ({{count}})'})} ▾
                    </button>
                </div>
                {error && <div className="inf-panel-state -error">{errorText(error, (code) => t(`informes.render.error.${code}`, t('informes.render.error.GENERIC', 'No se pudo calcular el informe.')), i18n.language)}</div>}
                {/* Los bloques que fallaron, resumidos UNA vez y con la acción que destraba. El
                    detalle de las cuentas caídas viaja sólo en el payload de agencia. */}
                <RenderWarningsNotice renderResult={result} agencyClientId={state.agencyClientId} showCta/>
                <EventsNotice accounts={eventsCheck.unconfigured} onConfigure={openEventsModal}/>
                <PanelsGrid renderResult={result} fieldsData={formData.fieldsData} loading={loading} panelRefsRef={panelRefsRef}
                            reportFailed={!!error}
                            hasUnconfiguredEvents={eventsCheck.unconfigured.length > 0}
                            onConfigureEvents={openEventsModal}
                            presets={presets} sourceType={state.settings.source_type}
                            onEditFields={(uid) => setFieldsOpen({scope: 'table', uid})}
                            onAddField={(uid, kind) => setFieldsOpen({scope: 'sheet', addFor: {uid, kind}})}/>
            </div>

            <InformePicker isOpen={pickerOpen} toggle={() => setPickerOpen(false)} agencyClientId={state.agencyClientId}
                           agencyClients={formData.agencyClients} currentInformeId={state.informe ? state.informe.id : null}
                           onNew={newInforme} onOpen={openInforme}/>
            <SaveInformeModal isOpen={saveOpen} toggle={() => setSaveOpen(false)} informe={state.informe} settings={state.settings}
                              template={state.template} templateModified={templateModified} clientName={clientName}
                              snapshotAt={snapshotAt} saving={saving} forceNew={saveAsNew} onConfirm={saveInforme}/>
            <SaveTemplateModal isOpen={templateModalOpen} toggle={() => setTemplateModalOpen(false)} template={state.template}
                               saving={saving} onConfirm={createTemplate}/>
            <FieldsModal isOpen={!!fieldsOpen} toggle={() => setFieldsOpen(null)} fieldsData={formData.fieldsData}
                         sourceType={state.settings.source_type} scope={fieldsOpen ? fieldsOpen.scope : 'sheet'}
                         fields={editableFields} clientHasGa4={clientHasGa4}
                         addingKind={fieldsOpen && fieldsOpen.addFor ? fieldsOpen.addFor.kind : null}
                         ga4SplitKept={!!(fieldsOpen && fieldsOpen.scope === 'table' && isGa4SplitKept(state, fieldsOpen.uid))}
                         ga4CrossLevels={ga4CrossLevels} dimensionsFilters={state.settings.dimensions_filters}
                         onKeepGa4Split={() => { if (fieldsOpen && fieldsOpen.uid) actions.keepGa4Split(fieldsOpen.uid); }}
                         onApply={(fields) => {
                             if (fieldsOpen && fieldsOpen.scope === 'table') actions.applyTableColumns(fieldsOpen.uid, fields);
                             else applyFieldsFrom(fieldsOpen, fields);
                             setFieldsOpen(null);
                         }}/>
            <DuplicateInformeModal isOpen={duplicateOpen} toggle={() => setDuplicateOpen(false)} informe={state.informe}
                                   settings={state.settings} layout={layoutPayload()} templateId={state.template.id}
                                   agencyClients={formData.agencyClients} currentClientId={state.agencyClientId}/>
            <ConfigureEventsModal isOpen={!!eventsModalAccounts} unconfiguredAccounts={eventsModalAccounts || []}
                                  onComplete={closeEventsModal} toggle={closeEventsModal}/>
            <UnsavedChangesModal isOpen={!!pendingOpen} informeDirty={informeDirty} sheetModified={sheetModified}
                                 hasTemplate={!!state.template.id}
                                 onCancel={() => setPendingOpen(null)} onDiscard={resolvePending}
                                 onSave={() => { setSaveAsNew(false); setSaveOpen(true); }}/>
            <UnsavedChangesModal leaving isOpen={leaveGuard.blocked} informeDirty={informeDirty} sheetModified={sheetModified}
                                 hasTemplate={!!state.template.id}
                                 onCancel={leaveGuard.stay} onDiscard={leaveGuard.leave}/>
            <ApplyTemplateConfirmModal isOpen={!!templateToConfirm}
                                       templateName={templateToConfirm ? templateToConfirm.name : null}
                                       onCancel={() => setTemplateToConfirm(null)}
                                       onConfirm={() => { const tpl = templateToConfirm; setTemplateToConfirm(null); applyTemplateNow(tpl); }}/>
        </div>
    );
}

export const INFORME_MODES = MODES;
